// @flow
import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Loader, withStyles } from '@phg/stilo-toolbox/components';
import { Label, Indicator } from '@phg/stilo-toolbox/v2/component';
import remoteMeta from 'app/state/ducks/remoteMeta';
import { STEPPER_SET_STEP } from 'app/state/reducers/application';

export type StepperProps = {
    id?: string,
    children: Array<React.Element<*>>,
    classes: {
        Container: string,
        Stepper: string,
        LoadingOverlay: string,
        ItemLabel: string,
        Indicator: string
    },
    loading: boolean,
    step: number,
    setStep: (number) => void,
    labels: Array<string>
};

export type StepperState = {
    count: number,
    total: number
};

const stepperContextType = {
    _stepper: PropTypes.shape({
        next: PropTypes.func.isRequired,
        prev: PropTypes.func.isRequired,
        first: PropTypes.func.isRequired,
        current: PropTypes.func.isRequired
    }).isRequired
};

const makeStyle = () => ({
    Container: {
        position: 'relative'
    },
    LoadingOverlay: {
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        position: 'absolute',
        width: '100%',
        top: 0,
        left: 0,
        height: '100%'
    },
    Stepper: {
        marginBottom: 20,
        width: '100%'
    },
    ItemLabel: {
        fontSize: 13,
        fontWeight: 500
    },
    Indicator: {
        marginRight: '-23%',
        marginLeft: '-23%',

        '& > *': {
            marginTop: 3
        }
    }
});

class Stepper extends React.Component<StepperProps, StepperState> {
    constructor(...props: *) {
        super(...props);

        // $FlowIgnore
        this.renderStepLabel = this.renderStepLabel.bind(this);
    }

    static childContextTypes = stepperContextType;

    static buildContext = (instance: Stepper) => ({
        _stepper: {
            next() {
                if (instance.state.total > instance.props.step) {
                    instance.props.setStep(instance.props.step + 1);
                }
            },
            prev() {
                if (instance.props.step > 0) {
                    instance.props.setStep(instance.props.step - 1);
                }
            },
            first() {
                if (instance.props.step > 0) {
                    instance.props.setStep(0);
                }
            },
            current() {
                return instance.props.step;
            }
        }
    });

    static countSteps(steps: Array<React.Element<*>>) {
        return steps.reduce((count, step) => {
            if (step.props.notCounted) {
                return count;
            }

            return count + 1;
        }, 0);
    }

    static getDerivedStateFromProps(props: StepperProps, state: StepperState) {
        if (state.total !== props.children.length) {
            return {
                count: Stepper.countSteps(props.children),
                total: props.children.length
            };
        }

        return null;
    }

    state = {
        count: 0,
        total: 0
    };

    _context = Stepper.buildContext(this);

    renderStepLabel(label, index) {
        return (
            <Label
                className={this.props.classes.ItemLabel}
                key={label+index}
                active={this.props.step === index ? 'true' : ''}
            >
                {label}
            </Label>
        );
    }

    getChildContext = () => this._context;

    render() {
        const { classes, id } = this.props;

        return (
            <React.Fragment>
                <Indicator id={id} className={classes.Indicator}>
                    {this.props.labels.map(this.renderStepLabel)}
                </Indicator>
                <div className={classes.Container}>
                    {this.props.children[this.props.step]}
                    {this.props.loading && (<div className={classes.LoadingOverlay}><Loader /></div>)}
                </div>
            </React.Fragment>
        );
    }
}

const ConnectedStepper = connect(
    (state) => ({
        loading: remoteMeta.selectors.hasPendingRquests(state),
        step: state.application.currentStep
    }),
    (dispatch) => ({
        setStep(step) {
            dispatch({ type: STEPPER_SET_STEP, payload: step });
        }
    })
)(Stepper);

export default withStyles(makeStyle)(ConnectedStepper);

